import React, { useCallback, memo } from "react";
// types
import { GiftingCouponCode } from "@swiggy-private/cart-types";
// styles
import {
    Wrapper,
    CouponCodePill,
    CouponCodeText,
    CouponCodeCounter,
    CounterText,
    PlusIconWrapper,
    TickIconWrapper,
} from "./styles";
// components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";
// constants
import { TEXTS } from "./constants";
import { SAVINGS_LINE_ITEM_TEST_IDS as IDS } from "@swiggy-private/cart-test-ids";
// utils
import { format, ElementObserver } from "@swiggy-private/core-ui";

interface CouponCodeListProps {
    applied?: boolean;
    codes?: GiftingCouponCode[];
    impressionEventCallback?: (couponCode: GiftingCouponCode, state?: boolean) => void;
}

const CouponCodeList = ({ applied, codes, impressionEventCallback }: CouponCodeListProps) => {
    const { SAVE, SAVED } = TEXTS;
    const text = applied ? SAVED : SAVE;
    const testId = applied ? IDS.COUPON_CODE_LIST_ITEM_APPLIED : IDS.COUPON_CODE_LIST_ITEM;

    const impressionEvent = useCallback(
        (couponCode: GiftingCouponCode) => {
            impressionEventCallback?.(couponCode, applied);
        },
        [applied, impressionEventCallback],
    );

    return (
        <Wrapper data-testid={IDS.COUPON_CODE_LIST}>
            {codes?.map((code, idx) => {
                const isLast = idx === codes.length - 1;
                const savingsAsNumber = Math.floor(Number(String(code.savings || "")));
                const savingsTexts = format(text, savingsAsNumber.toString());
                const dataTestId = format(testId, String(idx));
                const onViewEnter = () => impressionEvent(code);
                return (
                    <>
                        <ElementObserver key={`${code.code}-${idx}`} onViewEnter={onViewEnter}>
                            <CouponCodePill
                                key={`${code.code}-${idx}`}
                                applied={applied}
                                data-testid={dataTestId}
                                isLast={isLast}
                            >
                                {!applied && (
                                    <CouponCodeCounter>
                                        <CounterText variant="V2.caption.neutral.bold" color="primary">
                                            {idx + 1}
                                        </CounterText>
                                    </CouponCodeCounter>
                                )}
                                {applied && (
                                    <TickIconWrapper>
                                        <SpriteIconWeb
                                            iconName="tickFilled16"
                                            strokeColor="positive"
                                            fillColor="positive"
                                        />
                                    </TickIconWrapper>
                                )}
                                <CouponCodeText>
                                    <Text variant="V2.overline.neutral.bold" color="text_Low_Emphasis">
                                        {code.code}
                                    </Text>
                                    <Text variant="V2.overline.neutral.medium" color="text_Med_Emphasis">
                                        {savingsTexts}
                                    </Text>
                                </CouponCodeText>
                            </CouponCodePill>
                        </ElementObserver>
                        {!isLast && (
                            <PlusIconWrapper key={`${code.code}-${idx}-plus`}>
                                <Text variant="V2.subheading1.neutral.medium" color="text_High_Emphasis">
                                    {TEXTS.PLUS}
                                </Text>
                            </PlusIconWrapper>
                        )}
                    </>
                );
            })}
        </Wrapper>
    );
};

export default memo(CouponCodeList);
